import { useEffect, useMemo, useState } from 'react';
import {
  Alert, Button, Box, Card, CardContent, Chip, FormControlLabel, MenuItem, Switch, TextField,
  ToggleButton, ToggleButtonGroup, Typography, alpha,
} from '@mui/material';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { api } from '../../api/client';
import { useToast } from '../../components/ToastProvider';
import { errMessage } from '../../utils/errMessage';
import { PageHeader } from '../../components/PageHeader';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { useDeribitMarket, fmtDeribitPrice, type DeribitBookLevel } from '../../hooks/useDeribitData';
import { TradingViewChart } from '../../components/market/TradingViewChart';

type DeribitMarket = 'SPOT' | 'FUTURES';
type Side = 'BUY' | 'SELL';

interface Account { id: number; provider: string; label: string | null; status: string; access_mode: string | null; }
interface Instrument {
  symbol: string; base: string; quote: string; instrument: string;
  tickSize: number; minAmount: number; contractSize: number;
}
interface DeribitCatalog { spot: Instrument[]; perpetual: Instrument[]; }

function Stat({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <Box>
      <Typography sx={{ fontSize: 11, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</Typography>
      <Typography className="mono" sx={{ fontWeight: 700, fontSize: 14, color }}>{value}</Typography>
    </Box>
  );
}

/** Compact Deribit order-book ladder. Clicking a row fills the limit price. */
function DeribitBookLadder({ bids, asks, last, quote, onPick }: {
  bids: DeribitBookLevel[]; asks: DeribitBookLevel[]; last: number | undefined; quote: string; onPick: (p: number) => void;
}) {
  const Side = ({ levels, type }: { levels: DeribitBookLevel[]; type: 'bid' | 'ask' }) => {
    const color = type === 'bid' ? '#16c784' : '#ea3943';
    const max = Math.max(1, ...levels.map((l) => l.total));
    return (
      <Box>
        {levels.map((l, i) => (
          <Box key={i} onClick={() => onPick(l.price)} sx={{
            position: 'relative', display: 'flex', justifyContent: 'space-between',
            px: 1, py: '3px', cursor: 'pointer', fontSize: 12,
            '&:hover': { backgroundColor: 'rgba(255,255,255,0.04)' },
          }}>
            <Box sx={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: `${(l.total / max) * 100}%`, backgroundColor: `${color}1f` }} />
            <Typography className="mono" sx={{ color, fontSize: 12, zIndex: 1 }}>{fmtDeribitPrice(l.price)}</Typography>
            <Typography className="mono" sx={{ color: 'text.secondary', fontSize: 12, zIndex: 1 }}>{l.qty}</Typography>
          </Box>
        ))}
      </Box>
    );
  };
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 1, pb: 0.5, color: 'text.secondary', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
        <span>Price ({quote})</span><span>Amount</span>
      </Box>
      <Side levels={[...asks].reverse()} type="ask" />
      <Box sx={{ px: 1, py: 1, my: 0.5, borderBlock: '1px solid rgba(255,255,255,0.07)' }}>
        <Typography className="mono" sx={{ fontWeight: 700, fontSize: 18, color: '#eaecef' }}>{fmtDeribitPrice(last)}</Typography>
      </Box>
      <Side levels={bids} type="bid" />
    </Box>
  );
}

export default function DeribitTradePage() {
  const [params, setParams] = useSearchParams();
  const [market, setMarket] = useState<DeribitMarket>('FUTURES');
  const [symbol, setSymbol] = useState<string>(params.get('symbol') || '');
  const [side, setSide] = useState<Side>('BUY');
  const [accountId, setAccountId] = useState<number>(0);
  const [form, setForm] = useState({ quantity: '', amount: '', price: '', reduceOnly: false });
  const set = (k: string, v: unknown) => setForm((f) => ({ ...f, [k]: v }));
  const DEC_RE = /^\d*\.?\d*$/;
  const setNum = (k: string) => (e: { target: { value: string } }) => {
    if (DEC_RE.test(e.target.value)) set(k, e.target.value);
  };

  const toast = useToast();
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Deribit exchange accounts only — this section is dedicated to Deribit.
  const { data: accounts } = useQuery({
    queryKey: ['client-accounts'],
    queryFn: async () => (await api.get('/trading/accounts')).data.data.items as Account[],
  });
  const deribitAccounts = useMemo(
    () => (accounts ?? []).filter((a) => a.provider.toUpperCase() === 'DERIBIT'),
    [accounts],
  );
  const selectedAccount = deribitAccounts.find((a) => a.id === accountId) ?? null;
  const noAccounts = accounts && deribitAccounts.length === 0;
  // DIRECT accounts execute immediately; APPROVAL accounts queue for admin approval.
  const isDirect = selectedAccount?.access_mode === 'DIRECT';

  // Live Deribit instrument catalog (spot + perpetual) — only symbols Deribit
  // actually lists, so we never send a non-existent instrument again.
  const { data: catalog } = useQuery({
    queryKey: ['deribit-instruments'],
    queryFn: async () => (await api.get('/trading/markets/deribit')).data.data as DeribitCatalog,
    staleTime: 30 * 60 * 1000,
  });

  const list = useMemo(
    () => (market === 'SPOT' ? catalog?.spot : catalog?.perpetual) ?? [],
    [catalog, market],
  );
  const selected = list.find((i) => i.symbol === symbol) ?? list[0] ?? null;

  // Default the account once Deribit accounts load.
  useEffect(() => {
    if (deribitAccounts.length && !accountId) setAccountId(deribitAccounts[0].id);
  }, [deribitAccounts, accountId]);

  // Keep a valid symbol selected as the catalog loads or the market tab changes:
  // prefer keeping the same base across Spot/Perp, else fall back to the first.
  useEffect(() => {
    if (!list.length) return;
    if (list.some((i) => i.symbol === symbol)) return;
    const sameBase = selected ? list.find((i) => i.base === selected.base) : undefined;
    const next = sameBase?.symbol ?? list[0].symbol;
    setSymbol(next);
    setParams({ symbol: next }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list]);

  const changeSymbol = (s: string) => { setSymbol(s); setParams({ symbol: s }, { replace: true }); };

  const { ticker, book } = useDeribitMarket(selected?.instrument);
  const up = (ticker?.changePct ?? 0) >= 0;

  const base = selected?.base ?? '';
  const quote = selected?.quote ?? 'USDC';
  const markPrice = ticker?.price ?? 0;
  const effPrice = Number(form.price) || markPrice;
  const round2 = (n: number) => Math.round(n * 1e2) / 1e2;
  const round8 = (n: number) => Math.round(n * 1e8) / 1e8;

  const setQuantity = (v: string) => {
    if (!DEC_RE.test(v)) return;
    const q = Number(v);
    setForm((f) => ({ ...f, quantity: v, amount: effPrice > 0 && v !== '' ? String(round2(q * effPrice)) : f.amount }));
  };
  const setAmount = (v: string) => {
    if (!DEC_RE.test(v)) return;
    const amt = Number(v);
    setForm((f) => ({ ...f, amount: v, quantity: effPrice > 0 && v !== '' ? String(round8(amt / effPrice)) : f.quantity }));
  };
  const computedQty = Number(form.quantity);
  // Deribit trades each instrument only in whole multiples of its contract step
  // (e.g. ADA in steps of 10). Snap the requested quantity DOWN to a valid multiple
  // (never below the minimum) so what we submit equals what gets placed/filled —
  // a non-multiple like 65 would otherwise be adjusted by the exchange to 70.
  const step = selected?.contractSize ?? 0;
  const minAmt = selected?.minAmount ?? 0;
  const snapQty = (q: number): number => {
    if (!(q > 0)) return 0;
    if (!(step > 0)) return q;
    const decimals = Math.max(0, Math.ceil(-Math.log10(step)));
    const floored = Math.floor(q / step) * step;
    return Number(Math.max(minAmt, floored).toFixed(decimals));
  };
  const submitQty = snapQty(computedQty);
  // True when the entered quantity isn't already a valid step multiple (so we show
  // the adjusted amount before the user commits).
  const qtyAdjusted = computedQty > 0 && submitQty !== computedQty;

  const priceValid = Number(form.price) > 0;
  const [priceTouched, setPriceTouched] = useState(false);
  const showPriceError = priceTouched && !priceValid;

  // Re-sync the quote value when the effective price moves, anchoring on quantity.
  useEffect(() => {
    if (effPrice > 0) setForm((f) => ({ ...f, amount: f.quantity !== '' ? String(round2(Number(f.quantity) * effPrice)) : f.amount }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effPrice]);

  const place = useMutation({
    mutationFn: () =>
      api.post('/trading/orders', {
        accountId, market, symbol: selected?.symbol, side, type: 'LIMIT',
        quantity: submitQty, price: Number(form.price),
        ...(market === 'FUTURES' && {
          positionSide: side === 'BUY' ? 'LONG' : 'SHORT',
          reduceOnly: form.reduceOnly,
        }),
      }),
    onSuccess: () => toast.success(`${base} ${side} ${isDirect ? 'submitted — executing now' : 'submitted — pending admin approval'}`, 'Order submitted'),
    onError: (e) => toast.error(errMessage(e, 'Failed to submit order'), 'Order failed'),
    onSettled: () => setConfirmOpen(false),
  });
  const result = place.data?.data?.data;

  const buy = side === 'BUY';
  const sideColor = buy ? '#16c784' : '#ea3943';
  const futures = market === 'FUTURES';
  const canSubmit = !place.isPending && !noAccounts && !!accountId && !!selected && submitQty > 0 && priceValid;

  return (
    <Box>
      <PageHeader title="Deribit" subtitle="Dedicated Deribit ticket — live Deribit prices, spot & perpetual" />

      {noAccounts && (
        <Alert severity="info" sx={{ mb: 3 }}>
          You don't have a Deribit account connected yet. Ask your admin to add one, then trade Deribit spot & perpetuals here.
        </Alert>
      )}

      {/* Symbol + live Deribit price header */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ display: 'flex', alignItems: 'center', gap: { xs: 2, md: 4 }, flexWrap: 'wrap', py: 2 }}>
          <TextField
            select size="small" value={selected?.symbol ?? ''} onChange={(e) => changeSymbol(e.target.value)}
            sx={{ minWidth: 190 }} disabled={!list.length}
          >
            {list.map((i) => (
              <MenuItem key={i.instrument} value={i.symbol}>{i.base}/{i.quote}</MenuItem>
            ))}
          </TextField>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography className="mono" sx={{ fontWeight: 800, fontSize: 24, color: up ? '#16c784' : '#ea3943' }}>
              {fmtDeribitPrice(ticker?.price)}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', color: up ? '#16c784' : '#ea3943', fontWeight: 700 }}>
              {up ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
              {ticker ? `${up ? '+' : ''}${ticker.changePct.toFixed(2)}%` : '—'}
            </Box>
          </Box>
          <Stat label="Mark" value={fmtDeribitPrice(ticker?.markPrice)} />
          <Stat label="24h High" value={fmtDeribitPrice(ticker?.high)} />
          <Stat label="24h Low" value={fmtDeribitPrice(ticker?.low)} />
          <Stat label="24h Vol (USD)" value={ticker ? `${(ticker.volumeUsd / 1e6).toFixed(1)}M` : '—'} />
          <Chip size="small" label={selected ? selected.instrument : 'Deribit'} sx={{ ml: 'auto', fontFamily: 'monospace' }} />
        </CardContent>
      </Card>

      <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        {/* Chart — same TradingView Advanced Chart as the main trade panel. */}
        <Card sx={{ flex: '2 1 420px', minWidth: 300 }}>
          <CardContent sx={{ p: 1 }}>
            <TradingViewChart symbol={`${base || 'BTC'}USDT`} exchange="BYBIT" height={460} />
          </CardContent>
        </Card>

        {/* Order book */}
        <Card sx={{ flex: '1 1 240px', minWidth: 240 }}>
          <CardContent sx={{ p: 1.5 }}>
            <Typography variant="subtitle2" sx={{ px: 1, mb: 1 }}>Order Book</Typography>
            <DeribitBookLadder bids={book.bids} asks={book.asks} last={ticker?.price} quote={quote} onPick={(p) => set('price', String(p))} />
          </CardContent>
        </Card>

        {/* Order ticket */}
        <Card sx={{ flex: '1 1 320px', minWidth: 300 }}>
          <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 2.5 }}>
            <ToggleButtonGroup exclusive fullWidth size="small" value={market} onChange={(_, v) => v && setMarket(v)}>
              <ToggleButton value="SPOT">Spot</ToggleButton>
              <ToggleButton value="FUTURES">Perpetual</ToggleButton>
            </ToggleButtonGroup>

            <ToggleButtonGroup
              exclusive fullWidth value={side} onChange={(_, v) => v && setSide(v)}
              sx={{
                '& .Mui-selected[value="BUY"]': { color: '#fff', backgroundColor: alpha('#16c784', 0.22), borderColor: alpha('#16c784', 0.6), '&:hover': { backgroundColor: alpha('#16c784', 0.3) } },
                '& .Mui-selected[value="SELL"]': { color: '#fff', backgroundColor: alpha('#ea3943', 0.22), borderColor: alpha('#ea3943', 0.6), '&:hover': { backgroundColor: alpha('#ea3943', 0.3) } },
              }}
            >
              <ToggleButton value="BUY">{futures ? 'Long' : 'Buy'}</ToggleButton>
              <ToggleButton value="SELL">{futures ? 'Short' : 'Sell'}</ToggleButton>
            </ToggleButtonGroup>

            <TextField
              select size="small" label="Account" value={accountId || ''}
              onChange={(e) => setAccountId(Number(e.target.value))} disabled={noAccounts} fullWidth
            >
              {deribitAccounts.map((a) => (
                <MenuItem key={a.id} value={a.id}>{a.label || `Account #${a.id}`}</MenuItem>
              ))}
            </TextField>
            {selectedAccount && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: -1 }}>
                {isDirect ? (
                  <Chip size="small" label="Direct — no approval" sx={{ color: '#16c784', backgroundColor: alpha('#16c784', 0.14), border: `1px solid ${alpha('#16c784', 0.35)}` }} />
                ) : (
                  <Chip size="small" label="Approval Required" sx={{ color: '#f5a623', backgroundColor: alpha('#f5a623', 0.14), border: `1px solid ${alpha('#f5a623', 0.35)}` }} />
                )}
                <Chip size="small" label={selectedAccount.status} sx={{ color: selectedAccount.status === 'CONNECTED' ? '#16c784' : '#8b97b0', backgroundColor: alpha(selectedAccount.status === 'CONNECTED' ? '#16c784' : '#8b97b0', 0.14), border: `1px solid ${alpha(selectedAccount.status === 'CONNECTED' ? '#16c784' : '#8b97b0', 0.35)}` }} />
              </Box>
            )}

            <TextField select size="small" label="Order Type" value="LIMIT" disabled helperText="Only limit orders are accepted">
              <MenuItem value="LIMIT">Limit</MenuItem>
            </TextField>

            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <TextField
                fullWidth size="small" label={`Quantity (${base || 'base'})`}
                value={form.quantity} onChange={(e) => setQuantity(e.target.value)} inputProps={{ inputMode: 'decimal' }}
                error={qtyAdjusted}
                helperText={
                  qtyAdjusted
                    ? `Adjusted to ${submitQty} ${base} (steps of ${step})`
                    : (selected ? `Min ${selected.minAmount} · step ${selected.contractSize}` : ' ')
                }
              />
              <TextField
                fullWidth size="small" label={`Value (${quote})`}
                value={form.amount} onChange={(e) => setAmount(e.target.value)} inputProps={{ inputMode: 'decimal' }}
              />
            </Box>

            <TextField
              size="small" label={`Price (${quote})`} required placeholder="0.00"
              value={form.price}
              onChange={(e) => { setPriceTouched(true); setNum('price')(e); }}
              onBlur={() => setPriceTouched(true)}
              error={showPriceError}
              helperText={showPriceError ? 'Price is required for limit orders.' : (selected ? `Tick size ${selected.tickSize} · click a book row to autofill` : ' ')}
              inputProps={{ inputMode: 'decimal' }}
            />

            {futures && (
              <>
                <FormControlLabel
                  control={<Switch size="small" checked={form.reduceOnly} onChange={(e) => set('reduceOnly', e.target.checked)} />}
                  label="Reduce Only (close)"
                />
                <Alert severity="info" sx={{ py: 0.5 }}>
                  USDC-margined perpetual (one-way / net). Take-profit &amp; stop-loss are not supported on
                  Deribit. Leverage is set by the margin you commit — Deribit has no per-order leverage.
                </Alert>
              </>
            )}

            <Button
              variant="contained" size="large" color={buy ? 'success' : 'error'}
              disabled={!canSubmit} onClick={() => setConfirmOpen(true)}
              sx={{ py: 1.1, boxShadow: `0 8px 24px ${alpha(sideColor, 0.35)}` }}
            >
              {place.isPending ? 'Submitting…' : `${buy ? (futures ? 'Long' : 'Buy') : (futures ? 'Short' : 'Sell')} ${base}`}
            </Button>

            <ConfirmDialog
              open={confirmOpen}
              title={`Confirm ${futures ? (buy ? 'Long' : 'Short') : (buy ? 'Buy' : 'Sell')} order`}
              confirmLabel={`Confirm ${buy ? (futures ? 'Long' : 'Buy') : (futures ? 'Short' : 'Sell')}`}
              confirmColor={buy ? 'success' : 'error'}
              severity="warning"
              loading={place.isPending}
              onCancel={() => setConfirmOpen(false)}
              onConfirm={() => place.mutate()}
              warning={isDirect
                ? 'This account has DIRECT access — the order executes immediately on the live Deribit exchange with real funds, no approval required.'
                : 'This submits the order for admin approval. Once approved it executes on the live Deribit exchange with real funds.'}
            >
              <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', rowGap: 0.75, columnGap: 2 }}>
                <Typography variant="body2" color="text.secondary">Instrument</Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, textAlign: 'right' }} className="mono">{selected?.instrument}</Typography>
                <Typography variant="body2" color="text.secondary">Side / Type</Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, textAlign: 'right', color: sideColor }}>
                  {(futures ? (buy ? 'LONG' : 'SHORT') : side)} · LIMIT
                </Typography>
                <Typography variant="body2" color="text.secondary">Market</Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, textAlign: 'right' }}>{futures ? 'PERPETUAL' : 'SPOT'}</Typography>
                <Typography variant="body2" color="text.secondary">Quantity</Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, textAlign: 'right' }} className="mono">{submitQty} {base}</Typography>
                <Typography variant="body2" color="text.secondary">Est. Value</Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, textAlign: 'right' }} className="mono">≈ {round2(submitQty * (Number(form.price) || markPrice)) || 0} {quote}</Typography>
                <Typography variant="body2" color="text.secondary">Price</Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, textAlign: 'right' }} className="mono">{Number(form.price) || 0} {quote}</Typography>
                {futures && form.reduceOnly && (
                  <>
                    <Typography variant="body2" color="text.secondary">Reduce Only</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, textAlign: 'right' }}>Yes</Typography>
                  </>
                )}
              </Box>
            </ConfirmDialog>

            {result && (
              <Alert severity={result.status === 'PENDING' ? 'info' : 'success'}>
                {result.status === 'PENDING'
                  ? `Order pending admin approval (#${result.pendingId})`
                  : `Order submitted for execution (#${result.orderId})`}
              </Alert>
            )}
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}
